import React, { useState, useEffect } from 'react';
import Select, { components } from "react-select";
import { useCallback } from "react";
import Sidebar from './Sidebar';
import { Session } from "./utils/session";
import { API_BASE_URL } from '../config';
import { useNavigate } from "react-router-dom";

const Option = (props) => {
  return (
    <components.Option {...props}>
      <input
        type="checkbox"
        checked={props.isSelected}
        readOnly
        style={{
          margin: 0,
          marginRight: "10px" // gap between checkbox and label
        }}
      />
      <label style={{ margin: 0 }}>
        {props.label}
      </label>
    </components.Option>
  );
};
export default function DailyTaskList() {

  const role = Session.getRole();
  const adminRoles = ["Super Admin", "Admin", "Team Leader"];
  const isMember = !adminRoles.includes(role);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [hideDesktop, setHideDesktop] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [columns, setColumns] = useState({});
  const [selectedTask, setSelectedTask] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [taskSearch, setTaskSearch] = useState('');
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState("");
  const [selectedWorkspaceName, setSelectedWorkspaceName] = useState("");
  const [selectedSheetName, setSelectedSheetName] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const pageSize = 200;
  const [totalRecords, setTotalRecords] = useState(0);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [checkedTaskIds, setCheckedTaskIds] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedAssignUser, setSelectedAssignUser] = useState("");

  const totalPages = Math.ceil(totalRecords / pageSize) || 1;
  const start = (pageNumber - 1) * pageSize + 1;
  const end = Math.min(pageNumber * pageSize, totalRecords);
  const startPage = Math.floor((pageNumber - 1) / 5) * 5 + 1;
  const endPage = Math.min(startPage + 4, totalPages);
  const loginUserId = Session.getUserId();

  const navigate = useNavigate();

  const handleSheetChange = (e) => {
    const id = e.target.value;
    setTasks([]);
    setColumns({});
    setSelectedWorkspaceId(id);

    const ws = workspaces.find(w => String(w.workspaceID) === id);
    if (!ws) {
      setSelectedWorkspaceName("");
      setSelectedSheetName("");
      return;
    }

    setSelectedWorkspaceName(ws.workspaceName);
    setSelectedSheetName(ws.description);
    localStorage.setItem("selectedSheetId", id);
    localStorage.setItem("selectedSheetName", ws.description);
  };
  const handleSelectTask = (taskId) => {
    setCheckedTaskIds(prev =>
      prev.includes(taskId)
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const handleSelectAll = () => {
    if (checkedTaskIds.length === sortedTasks.length) {
      setCheckedTaskIds([]);
    } else {
      setCheckedTaskIds(sortedTasks.map(t => t.id));
    }
  };
  const getUserTypeBadge = (userType) => {
    const type = (userType || "").toLowerCase();

    if (type === "extraction")
      return (
        <span className="badge bg-primary-subtle text-primary-emphasis">
          Extraction
        </span>
      );

    if (type === "ror")
      return (
        <span className="badge bg-warning-subtle text-warning-emphasis">
          ROR
        </span>
      );

    if (type === "template")
      return (
        <span className="badge bg-info-subtle text-info-emphasis">
          Template
        </span>
      );

    if (type === "qc")
      return (
        <span className="badge bg-danger-subtle text-danger-emphasis">
          QC
        </span>
      );
    return (
      <span className="badge bg-secondary">
        {userType}
      </span>
    );
  };
  useEffect(() => {
    const loadWorkspaces = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/Workspace/Workspaces_List`, {
          headers: {
            Authorization: `Bearer ${Session.getToken()}`,
            "Content-Type": "application/json"
          }
        });
        const data = await res.json();
        setWorkspaces(Array.isArray(data) ? data : []);

        const savedId = localStorage.getItem("selectedSheetId");
        const ime = data.find(w => w.workspaceName === "IME");

        if (savedId) {
          setSelectedWorkspaceId(savedId);
          const ws = data.find(w => String(w.workspaceID) === savedId);
          if (ws) {
            setSelectedWorkspaceName(ws.workspaceName);
            setSelectedSheetName(ws.description);
          }
        } else if (ime) {
          setSelectedWorkspaceId(String(ime.workspaceID));
          setSelectedWorkspaceName(ime.workspaceName);
          setSelectedSheetName(ime.description);
        }
      } catch (err) {
        console.error("Workspace load error:", err);
      }
    };
    loadWorkspaces();
  }, []);

  useEffect(() => {
    const overlay = document.getElementById('sidebar-overlay');
    if (overlay) overlay.style.display = showMobileSidebar ? 'block' : 'none';
    const sidebar = document.getElementById('sidebar');
    if (sidebar) {
      if (showMobileSidebar) sidebar.classList.add('show-mobile');
      else sidebar.classList.remove('show-mobile');
      if (hideDesktop) sidebar.classList.add('hide-desktop');
      else sidebar.classList.remove('hide-desktop');
    }
    const content = document.getElementById('content');
    if (content) {
      if (hideDesktop) content.classList.add('full-width');
      else content.classList.remove('full-width');
    }
  }, [showMobileSidebar, hideDesktop]);

  const toggleSidebar = () => {
    if (window.innerWidth <= 991) setShowMobileSidebar(s => !s);
    else setHideDesktop(s => !s);
  };

  const handleRefresh = () => {
    setTaskSearch("");
    setSelectedUserId("");
    setFromDate("");
    setToDate("");
    setSelectedStatus("");
    setCheckedTaskIds([]);
    setPageNumber(1);
    loadCardsBySheet();
  };
  const loadCardsBySheet = useCallback(async () => {
    try {
      setLoading(true);
      if (!selectedWorkspaceName) { setLoading(false); return; }

      let apiUrl = "";
      if (selectedWorkspaceName === "IME") {
        let query = new URLSearchParams();
        query.append("pageNumber", pageNumber);
        query.append("pageSize", pageSize);
        query.append("roleName", role);
        query.append("loginUserID", loginUserId);
        if (taskSearch) query.append("searchText", taskSearch);
        if (selectedUserId === "unassigned") query.append("unassigned", 1);
        else if (selectedUserId) query.append("assignedUserId", selectedUserId);
        if (fromDate) query.append("fromDate", fromDate);
        if (toDate) query.append("toDate", toDate);
        apiUrl = `/api/Cards/CardsList-IME?${query.toString()}`;
      } else if (selectedWorkspaceName === "PHADS") {
        apiUrl = "/api/Cards/CardsList-PHAD";
      } else if (selectedWorkspaceName === "PEERS") {
        apiUrl = "/api/Cards/CardsList-PEER";
      } else {
        return;
      }

      const [statusRes, cardsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/CardStatus/list`, {
          headers: { Authorization: `Bearer ${Session.getToken()}`, "Content-Type": "application/json" }
        }),
        fetch(`${API_BASE_URL}${apiUrl}`, {
          headers: { Authorization: `Bearer ${Session.getToken()}`, "Content-Type": "application/json" }
        })
      ]);

      if (!statusRes.ok || !cardsRes.ok) { console.error("API Error"); return; }

      const statuses = await statusRes.json();
      setStatuses(statuses);
      const response = await cardsRes.json();
      const cardsList = response.data || [];
      setTotalRecords(response.totalRecords || 0);

      const colMap = {};
      statuses.forEach(s => { colMap[s.cardStatusID.toString()] = s.statusName; });
      setColumns(colMap);

      const flatTasks = cardsList.map(item => {
        let cardId, taskId;
        if (selectedWorkspaceName === "IME") {
          cardId = item.imeSheetID;
          taskId = `IME-${item.imeSheetID}-${item.userType}-${item.assignedUserId}`;
        } else {
          cardId = item.imeID || item.phadid || item.peersid || item.claimant;
          taskId = `card-${cardId}`;
        }
        return {
          id: taskId,
          content: item.docID || item.caseID || item.claimant,
          cardStatusID: item.cardStatusID,
          statusName: colMap[(item.cardStatusID || 1).toString()] || "",
          description: item.description || "",
          userType: item.userType,
          assignedUserId: item.assignedUserId,
          assignedUserIds: item.allAssignedUserIds ? String(item.allAssignedUserIds).split(",") : [],
          allAssignedUserIds: item.allAssignedUserIds,
          assignedUser: item.assignedUser,
          allAssignedUsers: item.allAssignedUsers,
          doctorName: item.doctorName,
          vendor: item.vendor,
          prioritys: item.prioritys,
          dueDate: item.date ? item.date.split("T")[0] : "",
          claimantPath: item.claimantPath,
          pageNo: item.pageNo,
        };
      });

      setTasks(flatTasks);
    } catch (err) {
      console.error("Board Load Error:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedWorkspaceName, taskSearch, selectedUserId, pageNumber, pageSize, fromDate, toDate, role, loginUserId]);

  useEffect(() => {
    if (!selectedWorkspaceName) {
      return;
    }
    fetch(`${API_BASE_URL}/api/Members/Users_List?workspaceName=${selectedWorkspaceName}`, {
      headers: { Authorization: `Bearer ${Session.getToken()}`, "Content-Type": "application/json" }
    })

      .then(res => res.json())
      .then(data => setUsers(data || []))
      .catch(err => console.error(err));
    console.log("selectedWorkspaceName =", selectedWorkspaceName);
  }, [selectedWorkspaceName]);

  useEffect(() => {
    if (selectedWorkspaceName) loadCardsBySheet();
  }, [taskSearch, selectedUserId, selectedWorkspaceName, pageNumber, fromDate, toDate, loadCardsBySheet]);

  useEffect(() => {
    const onResize = () => { if (window.innerWidth > 991) setShowMobileSidebar(false); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (selectedTask) {
      document.body.classList.add('modal-open');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = 'auto';
    };
  }, [selectedTask]);

  useEffect(() => {
    const interval = setInterval(() => { loadCardsBySheet(); }, 300000);
    return () => clearInterval(interval);
  }, [loadCardsBySheet]);

  const handleCardClick = (task) => setSelectedTask({ ...task });
  const handleCloseModal = () => setSelectedTask(null);

  const handleSaveClick = async () => {
    if (!selectedTask) return;
    setSaving(true);
    try {
      let apiUrl = "", payload = {};
      if (selectedWorkspaceName === "IME") {
        const parts = selectedTask.id.split("-");
        const imeId = parts[1];
        if (!imeId || !selectedTask.userType) { alert("Invalid IME data"); return; }
        apiUrl = "/api/Cards/Ime-card-update";
        payload = {
          IMESheetID: Number(imeId),
          UserType: selectedTask.userType,
          Description: selectedTask.description || "",
          AssignedUserID: selectedTask.assignedUserIds?.length > 0 ? selectedTask.assignedUserIds.join(",") : null,
          Prioritys: selectedTask.prioritys ? Number(selectedTask.prioritys) : null
        };
      } else {
        const cardId = Number(selectedTask.id.replace("card-", ""));
        if (!cardId) { alert("Invalid Card ID"); return; }
        apiUrl = "/api/Cards/card-update";
        payload = {
          sheetName: selectedSheetName,
          cardID: cardId,
          Description: selectedTask.description || "",
          assignedUserID: selectedTask.assignedUserId ? Number(selectedTask.assignedUserId) : null,
          Priority: selectedTask.prioritys ? Number(selectedTask.prioritys) : null,
          dueDate: selectedTask.dueDate ? new Date(selectedTask.dueDate).toISOString() : null,
          CreatedUserID: loginUserId
        };
      }
      const res = await fetch(`${API_BASE_URL}${apiUrl}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${Session.getToken()}` },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok || !data.success) { setSaving(false); alert(data.message || "Update failed."); return; }
      alert(data.message || "Updated Successfully.");
      setSelectedTask(null);
      await loadCardsBySheet();
    } catch (err) {
      console.error("Save Error:", err);
      alert("Update failed");
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!taskToDelete) return;
    const parts = taskToDelete.split("-");
    const imesheetid = Number(parts[1]);
    try {
      const res = await fetch(`${API_BASE_URL}/api/Cards/${imesheetid}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${Session.getToken()}` }
      });
      if (!res.ok) throw new Error("Delete failed");
      await loadCardsBySheet();
      setSelectedTask(null);
      setShowDeleteConfirm(false);
      setTaskToDelete(null);
    } catch (err) {
      console.error(err);
      alert("Failed to delete card");
    }
  };

  const handleDeleteClick = (taskId) => { setTaskToDelete(taskId); setShowDeleteConfirm(true); };
  const handleCancelDelete = () => { setShowDeleteConfirm(false); setTaskToDelete(null); };

  // Sorting
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedTasks = [...tasks].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const aVal = a[sortConfig.key] ?? "";
    const bVal = b[sortConfig.key] ?? "";
    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const SortIcon = ({ colKey }) => {
    if (sortConfig.key !== colKey) return <i className="fas fa-sort text-muted ms-1" style={{ fontSize: '0.7rem' }} />;
    return sortConfig.direction === 'asc'
      ? <i className="fas fa-sort-up ms-1" style={{ fontSize: '0.7rem' }} />
      : <i className="fas fa-sort-down ms-1" style={{ fontSize: '0.7rem' }} />;
  };

  const getPriorityBadge = (priority) => {
    if (!priority) return <span className="badge bg-secondary-subtle text-secondary-emphasis">—</span>;
    if (Number(priority) === 1) return <span className="badge bg-danger-subtle text-danger-emphasis">Rush</span>;
    return <span className="badge bg-warning-subtle text-warning-emphasis">{priority}</span>;
  };

  const getStatusBadge = (statusName) => {
    const lower = (statusName || "").toLowerCase();
    let cls = "bg-secondary-subtle text-secondary-emphasis";
    if (lower.includes("done")) cls = "bg-success-subtle text-success-emphasis";
    else if (lower.includes("progress") || lower.includes("process")) cls = "bg-primary-subtle text-primary-emphasis";
    else if (lower.includes("review")) cls = "bg-warning-subtle text-warning-emphasis";
    return <span className={`badge ${cls}`}>{statusName || "—"}</span>;
  };

  const handleMultipleUpdate = async () => {
    if (checkedTaskIds.length === 0) {
      alert("Please select at least one task.");
      return;
    }

    if (!selectedStatus) {
      alert("Please select a status.");
      return;
    }

    try {
      setLoading(true);

      const payload = checkedTaskIds.map((taskId) => {
        const task = tasks.find((t) => t.id === taskId);
        const parts = task.id.split("-");

        return {
          IMESheetID: Number(parts[1]),
          UserType: task.userType,
          DoneUserId: String(loginUserId),
          NewStatus: selectedStatus,
          IsTracking: false,
          ClaimantPath: task.claimantPath
        };
      });


      const res = await fetch(
        `${API_BASE_URL}/api/Cards/Ime-update-status-bulk`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Session.getToken()}`
          },
          body: JSON.stringify(payload)
        }
      );
      console.log(`${API_BASE_URL}/api/Cards/Ime-update-status-bulk`);
      const responseText = await res.text();

      let data = {};

      if (responseText) {
        try {
          data = JSON.parse(responseText);
        } catch {
          throw new Error("API returned an invalid response.");
        }
      }

      if (!res.ok || data.Success === false || data.success === false) {
        throw new Error(
          data.Message ||
          data.message ||
          `Request failed (${res.status})`
        );
      }

      alert(data.Message || data.message || "Task status updated successfully.");

      setCheckedTaskIds([]);
      setSelectedStatus("");

      await loadCardsBySheet();
    } catch (err) {
      console.error(err);
      alert(err.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAssign = async () => {

    if (checkedTaskIds.length === 0) {
      alert("Please select at least one task.");
      return;
    }

    if (!selectedAssignUser) {
      alert("Please select a user.");
      return;
    }

    try {
      setLoading(true);

      const payload = checkedTaskIds.map((taskId) => {
        const task = tasks.find((t) => t.id === taskId);
        const parts = task.id.split("-");

        return {
          IMESheetID: Number(parts[1]),
          UserType: task.userType,
          AssignedUserID: selectedAssignUser
        };
      });

      const res = await fetch(
        `${API_BASE_URL}/api/Cards/Ime-assign-user-bulk`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Session.getToken()}`
          },
          body: JSON.stringify(payload)
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Assignment failed.");
      }

      alert(data.message || "User assigned successfully.");

      setCheckedTaskIds([]);
      setSelectedAssignUser("");
      await loadCardsBySheet();

    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div>
      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(255,255,255,0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 99999
          }}
        >
          <img
            src="/Images/loader.gif"
            alt="loading"
            style={{ width: "80px" }}
          />
        </div>
      )}
      <div id="sidebar-overlay" onClick={() => setShowMobileSidebar(false)} />
      <div id="sidebar"><Sidebar /></div>
      <div id="content">
        <div className="d-flex align-items-center mb-3">
          <button type="button" id="sidebarCollapse" className="btn btn-toggle me-3" onClick={toggleSidebar}>
            <i className="fas fa-bars" />
          </button>
          <a href="/" className="text-muted text-decoration-none small"><i className="fas fa-chevron-left" /> Back</a>
        </div>

        <div className="d-flex justify-content-between align-items-center mb-4 page-header flex-wrap gap-2">
          <h2>  Tasks</h2>
          <div className="d-flex align-items-center gap-3 flex-wrap">
             <button className="btn btn-outline-primary btn-sm" onClick={handleRefresh}>
              Refresh </button>
            {/* View Toggle */}
            <div className="btn-group" role="group">
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() => navigate("/DailyTask")}
                title="Card View"
              >
                <i className="fas fa-th-large me-1" /> Card View
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                title="List View"
              >
                <i className="fas fa-list me-1" /> List View
              </button>
            </div>

            <div className="d-flex align-items-center gap-2">
              <label className="fw-semibold mb-0">Teams:</label>
              <select value={selectedWorkspaceId} onChange={handleSheetChange}>
                {workspaces.map((w) => (
                  <option key={w.workspaceID} value={w.workspaceID}>{w.workspaceName}</option>
                ))}
              </select>
            </div>           
          </div>
        </div>

        {/* Filters */}
        <div className="row g-2 mb-2 justify-content-end">

          <div style={{ width: "auto" }}>
            {!isMember && (
              <select
                className="form-select form-select-sm"
                value={selectedUserId}
                onChange={(e) => { setSelectedUserId(e.target.value); setPageNumber(1); }}
              >
                <option value="">All Users</option>
                <option value="unassigned">Unassigned</option>
                {users.map(u => <option key={u.userId} value={u.userId}>{u.userName}</option>)}
              </select>
            )}
          </div>

          <div style={{ width: "auto" }}>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search task"
              value={taskSearch}
              onChange={(e) => { setTaskSearch(e.target.value); setPageNumber(1); }}
            />
          </div>
          <div style={{ width: "auto" }}>
            <input
              type="date"
              className="form-control form-control-sm"
              value={fromDate}
              onChange={(e) => {
                const value = e.target.value;
                setFromDate(value);
                if (toDate && toDate < value) setToDate("");
                setPageNumber(1);
              }}
            />
          </div>
          <div style={{ width: "auto" }}>
            <input
              type="date"
              className="form-control form-control-sm"
              value={toDate}
              min={fromDate || ""}
              onChange={(e) => { setToDate(e.target.value); setPageNumber(1); }}
            />
          </div>

          <div style={{ width: "auto" }}>
            <div className="d-flex align-items-center gap-2 w-100">
              <select
                className="form-select form-select-sm"
                value={selectedStatus}  style={{ width: "180px" }}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value="">Status</option>

                {statuses.map((status) => (
                  <option
                    key={status.cardStatusID}
                    value={status.statusName}
                  >
                    {status.statusName}
                  </option>
                ))}
              </select>

              <button className="btn btn-primary btn-sm" style={{ width: "125px", }}
                disabled={checkedTaskIds.length === 0 || !selectedStatus} onClick={handleMultipleUpdate}> Save Status </button>
            </div>
          </div>

            <div style={{ width: "auto", display: "flex", alignItems: "center", gap: "8px" }}>
{!isMember && (
              <>
                <select
                  className="form-select form-select-sm"
                  style={{ width: "180px" }}
                  value={selectedAssignUser}
                  onChange={(e) => setSelectedAssignUser(e.target.value)}
                >
                  <option value="">Assign User</option>

                  {users.map(u => (
                    <option key={u.userId} value={u.userId}>
                      {u.userName}
                    </option>
                  ))}
                </select>

                <button
                  className="btn btn-primary btn-sm"  style={{ width: "125px", }}
                  disabled={
                    checkedTaskIds.length === 0 ||
                    !selectedAssignUser
                  }
                  onClick={handleBulkAssign}
                >
                  <i className="fas fa-user-plus me-1"></i>
                  Assign
                </button>
              </>
            )}
            </div>
        </div>

        {/* Table */}

        <div className="d-flex justify-content-end align-items-center mb-2 mt-4 gap-2">
          <span className="text-muted small me-2">Showing {start}–{end} of {totalRecords}</span>
          <nav>
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${pageNumber === 1 ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setPageNumber(p => p - 1)}>‹</button>
              </li>
              {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                const page = startPage + i;
                return (
                  <li key={page} className={`page-item ${page === pageNumber ? "active" : ""}`}>
                    <button className="page-link" onClick={() => setPageNumber(page)}>{page}</button>
                  </li>
                );
              })}
              <li className={`page-item ${pageNumber === totalPages ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setPageNumber(p => p + 1)}>›</button>
              </li>
            </ul>
          </nav>
        </div>

        <div className="table-responsive rounded-3 shadow-sm border">
          <table className="table table-hover table-bordered align-middle mb-0" style={{ fontSize: "0.875rem" }}>
            <thead className="table-light">
              <tr>
                <th style={{ width: "40px", textAlign: "center" }}>
                  <input type="checkbox"
                    checked={
                      sortedTasks.length > 0 &&
                      checkedTaskIds.length === sortedTasks.length
                    }
                    onChange={handleSelectAll}
                  />
                </th>

                <th style={{ width: "40px" }}>#</th>
                <th style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => handleSort("dueDate")}>
                  Date <SortIcon colKey="dueDate" />
                </th>
                <th style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => handleSort("content")}>
                  Case / Doc ID <SortIcon colKey="content" />
                </th>
                <th style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => handleSort("doctorName")}>
                  Doctor <SortIcon colKey="doctorName" />
                </th>
                <th style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => handleSort("pageNumber")}>
                  Page No <SortIcon colKey="pageNumber" />
                </th>
                <th style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => handleSort("vendor")}>
                  Vendor <SortIcon colKey="vendor" />
                </th>
                <th style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => handleSort("userType")}>
                  Type <SortIcon colKey="userType" />
                </th>
                <th style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => handleSort("assignedUser")}>
                  Assigned To <SortIcon colKey="assignedUser" />
                </th>
                <th style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => handleSort("prioritys")}>
                  Priority <SortIcon colKey="prioritys" />
                </th>
                <th style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => handleSort("statusName")}>
                  Status <SortIcon colKey="statusName" />
                </th>
                <th style={{ width: "80px", textAlign: "center" }}>Action</th>
              </tr>
            </thead>

            <tbody>

              {sortedTasks.length === 0 ? (
                <tr>
                  <td colSpan="12" className="text-center py-5 text-muted">
                    <i className="fas fa-inbox fa-2x mb-2 d-block" />
                    No tasks found
                  </td>
                </tr>
              ) : (
                sortedTasks.map((task, index) => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);

                  const caseDate = task.dueDate
                    ? new Date(task.dueDate)
                    : null;

                  if (caseDate) {
                    caseDate.setHours(0, 0, 0, 0);
                  }

                  const isFutureDone =
                    (task.statusName || "").toLowerCase() === "done" &&
                    caseDate &&
                    caseDate > today;

                  return (
                    <tr
                      key={task.id}
                      className={isFutureDone ? "future-done-row" : ""} >
                      <td className="text-center">
                        <input type="checkbox"
                          checked={checkedTaskIds.includes(task.id)}
                          onChange={() => handleSelectTask(task.id)}
                        />
                      </td>
                      <td className="text-muted">{start + index}</td>
                      <td style={{ whiteSpace: "nowrap" }}>{task.dueDate || "—"}</td>
                      <td>
                        <span className="fw-semibold">{task.content}</span>
                      </td>
                      <td>{task.doctorName || "—"}</td>
                      <td>{task.pageNo || "—"}</td>
                      <td>{task.vendor || "—"}</td>
                      <td>
                        {getUserTypeBadge(task.userType)}
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-1">
                          <img src="/Images/user.png" height="16" width="16" alt="" className="rounded-circle" />
                          <span className="badge bg-success-subtle text-success-emphasis" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                            {!task.assignedUser || task.assignedUser === "0" || task.assignedUser === 0 ? "Unassigned" : task.assignedUser}
                          </span>
                        </div>
                      </td>
                      <td>{getPriorityBadge(task.prioritys)}</td>
                      <td>{getStatusBadge(task.statusName)}</td>
                      <td className="text-center">
                        <button
                          className="btn btn-sm btn-outline-primary px-2 py-1"
                          title="View / Edit"
                          onClick={() => handleCardClick(task)}
                        >
                          <i className="fas fa-pen" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>


        {/* Edit Modal */}
        {selectedTask && (
          <>
            <div className="modal-backdrop fade show"></div>
            <div
              className="modal fade show"
              style={{ display: "block" }}
              tabIndex="-1"
              onClick={handleCloseModal}
            >
              <div className="modal-dialog modal-dialog-centered modal-lg" onClick={(e) => e.stopPropagation()}>
                <div className="modal-content border-0 shadow-lg">
                  {saving && (
                    <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 9999 }}>
                      <div className="spinner-border text-primary"></div>
                    </div>
                  )}
                  <div className="modal-header bg-light border-0 p-4">
                    <div className="d-flex align-items-center">
                      <span className="p-2 bg-primary-subtle text-primary-emphasis rounded-3 me-3">
                        <i className="fas fa-tasks fa-lg" />
                      </span>
                      <div>
                        <h5 className="modal-title mb-0 fw-bold">
                          {selectedTask.content} - {selectedTask.doctorName} - {selectedTask.vendor}
                        </h5>
                        <small className="text-muted">
                          Status: <span className="fw-semibold">{selectedTask.statusName}</span>
                        </small>
                      </div>
                    </div>
                    <button type="button" className="btn-close" onClick={handleCloseModal}></button>
                  </div>

                  <div className="modal-body p-4">
                    <div className="row mb-4">
                      <div className="col-md-6">
                        <h6 className="fw-bold text-muted text-uppercase small mb-2">Claimant / Case ID</h6>
                        <textarea className="form-control" rows="3" value={selectedTask.content} readOnly />
                      </div>
                      <div className="col-md-6">
                        <h6 className="fw-bold text-muted text-uppercase small mb-2">Description</h6>
                        <textarea
                          className="form-control"
                          rows="3"
                          value={selectedTask.description || ""}
                          onChange={(e) => setSelectedTask(prev => ({ ...prev, description: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="row g-4">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <h6 className="fw-semibold text-muted text-uppercase small mb-2">Assigned To</h6>
                          <div className="d-flex align-items-center">
                            <img src="/Images/user.png" height="20" width="20" alt="" className="rounded-circle me-2" />
                            {isMember ? (
                              <input
                                type="text"
                                className="form-control"
                                value={selectedTask?.assignedUser || ""}
                                readOnly
                              />
                            ) : (
                              <Select
                                isMulti
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                components={{
                                  Option,
                                  ClearIndicator: () => null,
                                  IndicatorSeparator: () => null,
                                  MultiValueRemove: () => null
                                }}
                                styles={{
                                  container: (provided) => ({
                                    ...provided,
                                    width: "100%",
                                    flex: 1,
                                  }),

                                  option: (provided, state) => ({
                                    ...provided,
                                    backgroundColor: state.isSelected
                                      ? "#fff"           // selected hoy to white
                                      : state.isFocused
                                        ? "#f5f5f5"        // hover color
                                        : "#fff",

                                    color: "#333",

                                    ":active": {
                                      backgroundColor: "#f5f5f5",
                                    },
                                  }),
                                }}
                                options={users.map((u) => ({
                                  value: String(u.userId),
                                  label: u.userName
                                }))}
                                value={users
                                  .filter((u) =>
                                    selectedTask?.assignedUserIds?.includes(String(u.userId))
                                  )
                                  .map((u) => ({
                                    value: String(u.userId),
                                    label: u.userName
                                  }))
                                }
                                onChange={(selectedOptions) => {
                                  setSelectedTask((prev) => ({
                                    ...prev,
                                    assignedUserIds:
                                      selectedOptions?.map((x) => x.value) || [],
                                    assignedUser:
                                      selectedOptions?.map((x) => x.label).join(", ") || ""
                                  }));
                                }}
                              />
                            )}
                          </div>
                        </div>
                        <div>
                          <h6 className="fw-semibold text-muted text-uppercase small mb-2">Date</h6>
                          <input
                            type="date"
                            className="form-control bg-light"
                            value={selectedTask?.dueDate ?? ""}
                            readOnly
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <h6 className="fw-semibold text-muted text-uppercase small mb-2">Priority</h6>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter priority"
                            value={selectedTask?.prioritys ?? ""}
                            onChange={(e) => setSelectedTask(prev => ({ ...prev, prioritys: e.target.value }))}
                          />
                        </div>
                        <div>
                          <h6 className="fw-semibold text-muted text-uppercase small mb-2">Tags</h6>
                          <div className="d-flex flex-wrap gap-2">
                            {selectedTask?.userType && (
                              <span className="badge bg-primary-subtle text-primary-emphasis px-3 py-1">{selectedTask.userType}</span>
                            )}
                            {Number(selectedTask?.prioritys) === 1 && (
                              <span className="badge bg-danger-subtle text-danger-emphasis px-3 py-1">Rush Case</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer bg-light border-0">
                    {!isMember && (
                      <>
                        <button type="button" className="btn btn-primary" onClick={handleSaveClick}>
                          <i className="fas fa-save me-1"></i> Save Changes
                        </button>
                        <button type="button" className="btn btn-outline-danger" onClick={() => handleDeleteClick(selectedTask.id)}>
                          <i className="fas fa-trash me-1"></i> Delete
                        </button>
                      </>
                    )}
                    <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>Close</button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Delete Confirm */}
        {showDeleteConfirm && (
          <>
            <div className="modal-backdrop fade show" style={{ zIndex: 1060 }}></div>
            <div className="modal fade show" style={{ display: 'block', zIndex: 1065 }} tabIndex="-1">
              <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '400px' }}>
                <div className="modal-content border-0 shadow-lg">
                  <div className="modal-body p-4 text-center">
                    <div className="mb-3"><i className="fas fa-exclamation-triangle fa-3x text-danger"></i></div>
                    <h5 className="fw-bold">Are you sure?</h5>
                    <p className="text-muted">Do you really want to delete this task? This process cannot be undone.</p>
                  </div>
                  <div className="modal-footer border-0 bg-light justify-content-center">
                    <button type="button" className="btn btn-secondary" onClick={handleCancelDelete}>Cancel</button>
                    <button type="button" className="btn btn-danger" onClick={handleConfirmDelete}>Yes, Delete</button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}